import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { MAPS_API_KEY } from "../../components/mapKey";
import url from "../../url";
import { Box, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";

const LIBRARIES = ["places"]; // ✅ outside component

const getStatusIcon = (status) => {  // ✅ outside component
  const icons = {
    active:    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
    inactive:  "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png",
    invited:   "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
    pending:   "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
    requested: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png",
  };
  return icons[status] || icons.active;
};

const WorkersPins = ({
  statusFilter, dateFrom, dateTo, selectedCompanyId,
  filterCountry, filterState, filterCity, search,
  verificationStatusFilter, selectedMapWorkerId, onClearSelection,
}) => {
  const [workers, setWorkers] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [isFullMap, setIsFullMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 30.3753, lng: 69.3451 });
  const [mapZoom, setMapZoom] = useState(5);

  const { token } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const mapRef = useRef(null); // ✅ map instance ref

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY,
    libraries: LIBRARIES, // ✅ stable reference
  });

  useEffect(() => {
    const fetchAllWorkers = async () => {
      setLoading(true);
      try {
        const statusParam = statusFilter !== "all" ? `&status=${statusFilter}` : "";
        const dateFromParam = dateFrom ? `&date_from=${dateFrom}` : "";
        const dateToParam = dateTo ? `&date_to=${dateTo}` : "";
        const companyParam = selectedCompanyId ? `&company_id=${selectedCompanyId}` : "";
        const countryParam = filterCountry ? `&country=${filterCountry}` : "";
        const stateParam = filterState ? `&state=${filterState}` : "";
        const cityParam = filterCity ? `&city=${filterCity}` : "";
        const searchParam = `&search=${search}`;
        const verificationStatusParam = verificationStatusFilter && verificationStatusFilter !== "all"
          ? `&verification_status=${verificationStatusFilter}` : "";

        const apiUrl = `${url}public/workers?no_pagination=true`
          + statusParam + dateFromParam + dateToParam + companyParam
          + countryParam + stateParam + cityParam + searchParam + verificationStatusParam;

        const response = await fetch(apiUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json();

        if (!result?.error && Array.isArray(result?.data?.records)) {
          const data = result.data.records
            .map((worker) => ({
              id: worker.id,
              name: worker.first_name || worker.email,
              email: worker.email,
              status: worker.status,
              lat: parseFloat(worker.latitude),
              lng: parseFloat(worker.longitude),
            }))
            .filter((e) => !isNaN(e.lat) && !isNaN(e.lng));
          setWorkers(data);
        } else {
          setWorkers([]);
        }
      } catch (error) {
        console.error("Error fetching workers:", error);
        setWorkers([]);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchAllWorkers();
  }, [token, statusFilter, dateFrom, dateTo, selectedCompanyId, filterCountry, filterState, filterCity, search, verificationStatusFilter]);

  // ✅ Auto-center, zoom and open InfoWindow when row selected from table
  useEffect(() => {
    if (selectedMapWorkerId && workers.length > 0) {
      const selected = workers.find((w) => w.id === selectedMapWorkerId);
      if (selected) {
        if (mapRef.current) {
          mapRef.current.panTo({ lat: selected.lat, lng: selected.lng });
          mapRef.current.setZoom(10);
        }
        setMapCenter({ lat: selected.lat, lng: selected.lng });
        setMapZoom(10);
        // setSelectedMarker(selected);
      }
    } else {
      // ✅ Reset map when selection cleared
      setMapCenter({ lat: 30.3753, lng: 69.3451 });
      setMapZoom(5);
      setSelectedMarker(null);
    }
  }, [selectedMapWorkerId, workers]);

  const displayedWorkers = selectedMapWorkerId
    ? workers.filter((w) => w.id === selectedMapWorkerId)
    : workers;

  const onMapLoad = (map) => {
    mapRef.current = map; // ✅ store map instance
  };

  // ✅ renderMarkers as function — not component
  const renderMarkers = () => (
    <>
      {displayedWorkers.map((worker) => (
        <Marker
          key={worker.id}
          position={{ lat: worker.lat, lng: worker.lng }}
          icon={{
            url: getStatusIcon(worker.status),
            scaledSize: new window.google.maps.Size(25, 41),
            anchor: new window.google.maps.Point(12, 41),
          }}
          onClick={() => setSelectedMarker(worker)} // ✅ onClick not onMouseOver
        />
      ))}

      {selectedMarker && (
        <InfoWindow
          position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
          onCloseClick={() => setSelectedMarker(null)} // ✅ onCloseClick
        >
          <div style={{
            backgroundColor: "white", borderRadius: "8px",
            padding: "6px 10px", minWidth: "150px",
            boxShadow: "0px 2px 6px rgba(0,0,0,0.2)", textAlign: "center",
          }}>
            <div style={{ fontWeight: "bold", fontSize: "14px", color: "#333" }}>
              {selectedMarker.name}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>
              {selectedMarker.email}
            </div>
          </div>
        </InfoWindow>
      )}
    </>
  );

  return (
    <div style={{
      height: "70vh", width: "100%", position: "relative", marginTop: "10px",
      borderRadius: "10px", backgroundColor: "white",
      display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column"
    }}>

      {loading && (
        <Box sx={{
          position: "absolute", inset: 0, zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "rgba(255,255,255,0.6)",
        }}>
          <CircularProgress size={50} />
        </Box>
      )}

      {/* ✅ Normal map — unmounts when full map opens */}
      {isLoaded && !isFullMap && (
        <GoogleMap
          mapContainerStyle={{ height: "100%", width: "100%", borderRadius: "10px" }}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad} // ✅ capture map instance
          options={{
            // ✅ Lock map when a pin is selected from table
            gestureHandling: selectedMapWorkerId ? "none" : "auto",
            zoomControl: !selectedMapWorkerId,
            scrollwheel: !selectedMapWorkerId,
            disableDoubleClickZoom: !!selectedMapWorkerId,
            draggable: !selectedMapWorkerId,
          }}
        >
          {renderMarkers()}
        </GoogleMap>
      )}

      {/* Full Map Button */}
      {!isFullMap && (
        <Box
          onClick={() => setIsFullMap(true)}
          sx={{
            position: "absolute", top: 10, right: 10, zIndex: 1000,
            backgroundColor: "#006EC2", color: "white", borderRadius: "6px",
            px: 1.5, py: 0.5, cursor: "pointer", fontSize: "14px", fontWeight: 500,
          }}
        >
          {t("fullMap")}
        </Box>
      )}

      {/* Clear Selection Button */}
      {selectedMapWorkerId && !isFullMap && (
        <Box
          onClick={() => {
            setIsFullMap(false);
            onClearSelection();
          }}
          sx={{
            position: "absolute", top: 55, right: 10, zIndex: 3000,
            backgroundColor: "#fff", borderRadius: "50%", width: 36, height: 36,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
            fontSize: "18px", fontWeight: 700,
          }}
        >
          ✕
        </Box>
      )}

      {/* ✅ Full Map Modal — only mounts when isFullMap is true */}
      {isFullMap && isLoaded && (
        <Box sx={{ position: "fixed", inset: 0, zIndex: 2000, backgroundColor: "#fff" }}>
          <Box
            onClick={() => setIsFullMap(false)}
            sx={{
              position: "absolute", top: 15, right: 15, zIndex: 3000,
              backgroundColor: "#fff", borderRadius: "50%", width: 36, height: 36,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", boxShadow: "0px 2px 6px rgba(0,0,0,0.3)",
              fontSize: "18px", fontWeight: 700,
            }}
          >
            ✕
          </Box>

          <GoogleMap
            mapContainerStyle={{ height: "100vh", width: "100vw" }}
            center={mapCenter}
            zoom={mapZoom}
            onLoad={(map) => { mapRef.current = map; }}
            options={{
              gestureHandling: selectedMapWorkerId ? "none" : "auto",
              zoomControl: !selectedMapWorkerId,
              scrollwheel: !selectedMapWorkerId,
              disableDoubleClickZoom: !!selectedMapWorkerId,
              draggable: !selectedMapWorkerId,
            }}
          >
            {renderMarkers()}
          </GoogleMap>
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
        {t("Only records with valid Latitude/Longitude are shown")}
      </Typography>
    </div>
  );
};

export default WorkersPins;